import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BreakableRenderer, BusinessComponent, BusinessComponentOptions, ColumnComposition, ColumnCompositions, RowPanel } from '@blk/simba';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { ComponentData, DateFormatter, TabData, TableData } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';
import { HeadingLevel, HeadingWithDateComponent } from '@ddt-web/pdf-components';
import * as get from 'lodash/get';

import { FamilyHoldingsHeaderColumnComposition } from '../helpers/column-compositions';

export class FamilyLatestHoldingsHeaderComponent extends BusinessComponent {
	@InjectConfig(ModelsExperienceConfigKey.HOLDINGS_TABLE_HEADER_COLUMNS) private readonly holdingsTableHeaderColumns = ['expenseRatio', 'grossExpenseRatio'];
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_HOLDINGS_TABLE_HEADER_V2) private readonly enableHoldingsTableHeaderV2: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	@InjectConfig(ModelsExperienceConfigKey.USE_H1_TITLE_PDF) private readonly useH1Title: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.HIDE_LATEST_ALLOCATION_FAMILY_LIST) private readonly hideLatestAllocationFamilies = [];


	private headersLabel: string[];
	private readonly HOLDINGS_TABLE_INITIAL_HEADERS_AU = {
		TARGET_ALLOCATION: 'targetAllocation',
		LATEST_ALLOCATION: 'latestAllocation',
		COMPOSITE_FEE: 'compositeFee'
	};
	public latestHoldingsColumns = [];
	constructor(
		name: string,
		public dataComponentId: string,
		public visible: boolean = true,
		public modelNames: string[] = [],
		public asOfDate: string,
		public latestAllocationData: string[] = [],
		public familyId: string
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		return componentData && componentData.success;
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		if (this.isComponentDataVisible(componentData)) {
			this.headersLabel = ['', contentSvc.get('AS_OF_DATE', 'As of Date'), ...this.modelNames];
			const tabData = this.enableHoldingsTableHeaderV2 ? componentData.getTabById('holdingsTable.header.v2') : componentData.getTabById('latestHoldings.header');
			const panels: AbstractRenderer<SVGGElement>[] = [];
			const headerKey = this.enableHoldingsTableHeaderV2 ? 'PDF_' + this.name.trim().toUpperCase().replace('', '_') + '_H2' : 'PDF_LATEST_HOLDING_H2';
			const headingLevel = this.useH1Title ? HeadingLevel.H1 : HeadingLevel.H2;
			panels.push(
				new HeadingWithDateComponent().buildHeader(
					'Heading', headerKey, 'Latest Holdings (%)',
					new DateFormatter(this.dateFormat).format(this.asOfDate),
					'ALLOCATIONS_AS_OF_PDF',
					true,
					headingLevel
				)
			);
			panels.push(...this.getTablePanels(tabData, this.latestAllocationData));

			return panels;
		}
		return [];
	}

	private getTablePanels(tabData: TabData, latestAllocationData: string[]): Array<AbstractRenderer<SVGGElement>> {
		const panels: Array<AbstractRenderer<SVGGElement>> = [];
		const asOfDateData = this.getAsOfDateForAllData(tabData);
		const columnConfigurations: ColumnCompositions
			= new FamilyHoldingsHeaderColumnComposition().getModelColumnCompositions(
				tabData, this.headersLabel, asOfDateData, this.familyId, latestAllocationData
			);
		const rows: Array<RowPanel> = [];

		const headerRow: RowPanel = new RowPanel(
			'Model Names Row',
			columnConfigurations.headers.map((config: ColumnComposition) => config.renderer.render()),
			{ paddingLeft: 5 },
			Alignment.Horizontal.Right,
			Alignment.Vertical.Middle
		);

		rows.push(headerRow);

		columnConfigurations.body.forEach((configs: Array<ColumnComposition>) => {
			const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
			rows.push(
				new RowPanel(
					'Model Details Table Rows',
					rowRenderer,
					{ paddingLeft: 5, backgroundColor: '#FFFFFF' },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Middle
				)
			);
		});

		panels.push(new BreakableRenderer(rows, {}, headerRow));
		return panels;
	}

	private getAsOfDateForAllData(tabData: TabData): string[] {
		const result = [];
		const tableData: TableData = get(tabData, 'tableData[0]');
		if (this.enableHoldingsTableHeaderV2) {
			this.latestHoldingsColumns = this.hideLatestAllocationFamilies.includes(this.familyId) ?
				this.holdingsTableHeaderColumns.filter(column => column !== this.HOLDINGS_TABLE_INITIAL_HEADERS_AU.LATEST_ALLOCATION) : this.holdingsTableHeaderColumns;

			this.latestHoldingsColumns.forEach(item => {
				if (item === this.HOLDINGS_TABLE_INITIAL_HEADERS_AU.TARGET_ALLOCATION) {
					result.push(new DateFormatter(this.dateFormat).format(tableData?.['rows']?.[0].targetAllocationAsOfDate.formattedValue));
				}
				if (item === this.HOLDINGS_TABLE_INITIAL_HEADERS_AU.LATEST_ALLOCATION) {
					result.push(new DateFormatter(this.dateFormat).format(this.asOfDate));
				}
				if (item === this.HOLDINGS_TABLE_INITIAL_HEADERS_AU.COMPOSITE_FEE) {
					result.push(new DateFormatter(this.dateFormat).format(tableData?.['rows']?.[0].compositeFeeAsOfDate.formattedValue));
				}
			});
		} else {
			result.push(new DateFormatter(this.dateFormat).format(get(tableData, 'rows[0].expenseRatioAsOfDate.formattedValue'))); // net expenses ratio
			result.push(new DateFormatter(this.dateFormat).format(get(tableData, 'rows[0].expenseRatioAsOfDate.formattedValue'))); // gross expenses ratio
			result.push(new DateFormatter(this.dateFormat).format(get(tableData, 'rows[0].ytdReturnAsOfDate.formattedValue'))); // ytd return
			result.push(new DateFormatter(this.dateFormat).format(get(tableData, 'rows[0].estimatedRiskAsOfDate.formattedValue'))); // estimated risk
		}
		return result;
	}
}
